import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion, Button, ContentSection, Heading, Layout, PageWrap } from '@vwfs-bronson/bronson-react';
import { useRemoveCardTitle } from '../../services/common/useRemoveCardTitle';
import { RootState } from '../../services/redux';
import { resetNotes, setCurrentConversation } from '../../services/redux/features/notes.slice';
import { DashboardSubpagesTabs } from '../Dashboard/subpages/components/DashboardSubpagesTabs';
import { ChatMessage } from './ChatMessage';
import { Conversation } from './Conversation';
import { ConversationListItem } from './ConversationListItem';
import { Filters } from './Filters';
import { NewConversationCard } from './NewConversationCard';
import { useConversations } from './useConversations';

interface NotesPageProps {}

export interface MessageProps {
  author: string;
  message: string;
  time: Date;
  userRole: MilesPosNotesIntegrationService.Components.Schemas.ParticipantType;
  isCurrentUser: boolean;
  messageType?: 'SYSTEM_MESSAGE' | 'TEXT_MESSAGE';
}

export const Message: FunctionComponent<MessageProps & { showHeader?: boolean }> = (props) => {
  return <ChatMessage {...props} />;
};

export const NotesPage: FunctionComponent<NotesPageProps> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isCreatingNewConversationModeEnabled, setIsCreatingNewConversationModeEnabled] = useState<boolean>(false);
  const [isCreatingNewConversationRequestPending, setIsCreatingNewConversationRequestPending] =
    useState<boolean>(false);
  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>(['filters-accordion']);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const intent = useSelector((state: RootState) => state.notes.intent);
  const conversations = useSelector((state: RootState) => state.notes.conversations);
  const activeConversation = useSelector((state: RootState) => state.notes.currentConversation);
  const groupedMessages = useSelector((state: RootState) => state.notes.currentConversationMessages);
  const isSendingMessage = useSelector((state: RootState) => state.notes.isSendingMessage);
  const filters = useSelector((state: RootState) => state.notes.filters);
  const conversationDocumentMetadata = useSelector((state: RootState) => state.notes.conversationDocumentMetadata);

  const titleOptions = [
    { value: 'Acceptation', label: 'Acceptation' },
    { value: 'Paiement', label: 'Paiement' },
    { value: 'Rejet de document', label: 'Rejet de document' },
    { value: 'Bon Ă  livrer', label: 'Bon Ă  livrer' },
  ];

  useRemoveCardTitle();

  useEffect(() => {
    return () => {
      dispatch(resetNotes());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    createConversation,
    addMessageAndJoinIfNeeded,
    fetchMessages,
    fetchConversations,
    isUpdatingTitle,
    updateConversationTitle,
    updateConversationStatus,
    isUpdatingStatus,
  } = useConversations();

  useEffect(() => {
    fetchConversations(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeConversation?.conversationId) {
      fetchMessages(activeConversation.conversationId, activeConversation.participants);
    }
  }, [activeConversation, fetchMessages]);

  useEffect(() => {
    if (intent.type === 'READ' && intent.conversationId) {
      const conversationFromTheList = conversations.find(
        (conversation) => conversation.conversationId === intent.conversationId
      );
      if (conversationFromTheList) {
        dispatch(setCurrentConversation(conversationFromTheList));
      }
    }
  }, [intent, activeConversation, conversations, dispatch]);

  useEffect(() => {
    if (intent.type === 'CREATE_CONVERSATION') {
      setIsCreatingNewConversationModeEnabled(true);
    }

    if (intent.type === 'READ') {
      setIsCreatingNewConversationModeEnabled(false);
    }
  }, [intent, setIsCreatingNewConversationModeEnabled]);

  const handleCreateConversation = async (title: string, message: string) => {
    if (!title.trim() || !message.trim()) {
      setIsCreatingNewConversationModeEnabled(false);
      return;
    }

    setIsCreatingNewConversationRequestPending(true);
    try {
      await createConversation(title, message, filters);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to create conversation:', error);
    } finally {
      setIsCreatingNewConversationRequestPending(false);
      setIsCreatingNewConversationModeEnabled(false);
    }
  };

  const handleAddMessage = async () => {
    if (isSendingMessage) {
      return;
    }
    const messageText = messageInputRef.current?.value.trim();
    if (!activeConversation?.conversationId || !messageText) {
      return;
    }

    const result = await addMessageAndJoinIfNeeded(activeConversation, messageText);

    if (result.success && messageInputRef.current) {
      messageInputRef.current.value = '';
    }
  };

  return (
    <>
      <ContentSection pageWrap className="u-pt-none">
        <DashboardSubpagesTabs />
      </ContentSection>

      <PageWrap>
        <Layout className="row">
          <Layout.Item default="1/3" s="1/1">
            {/* Filters */}
            <div className="u-mb-large">
              <Accordion openItems={openAccordionItems} onChange={setOpenAccordionItems} itemKeyProp="id">
                <Accordion.Item id="filters-accordion" title={t('filters:filter')}>
                  <Filters
                    onSubmit={() => {
                      fetchConversations(filters);
                      setOpenAccordionItems([]);
                    }}
                    onReset={(filters) => fetchConversations(filters)}
                    titleOptions={titleOptions}
                  />
                </Accordion.Item>
              </Accordion>
            </div>

            {/* Conversations List */}
            <div>
              <Heading level={4}>
                {t('notesPage:results')}
                {conversations.length}
              </Heading>

              <div className="u-p-xsmall" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                {conversations.map((conversationItem) => (
                  <ConversationListItem
                    key={conversationItem.conversationId}
                    conversationItem={conversationItem}
                    isActiveConversation={activeConversation?.conversationId === conversationItem.conversationId}
                  />
                ))}
              </div>
            </div>
          </Layout.Item>

          <Layout.Item default="2/3" s="1/1" className="u-mt@s">
            <Layout>
              <Layout.Item default="1/2">
                {conversationDocumentMetadata ? (
                  <Heading level={4}>
                    {t('notesPage:notesFor', {
                      entityFullName: conversationDocumentMetadata.entityFullName,
                      humanReadableObjectReferenceId:
                        conversationDocumentMetadata.humanReadableObjectReferenceId ||
                        filters.humanReadableObjectReferenceId,
                    })}
                  </Heading>
                ) : (
                  <Heading level={4}>{t('notesPage:objectWithNotes')}</Heading>
                )}
              </Layout.Item>
              <Layout.Item default="1/2" className="u-text-right">
                {conversationDocumentMetadata && (
                  <Button
                    icon="semantic-plus"
                    iconReversed
                    onClick={() => setIsCreatingNewConversationModeEnabled(true)}
                    disabled={isCreatingNewConversationModeEnabled}
                  >
                    {t('notesPage:createConversation')}
                  </Button>
                )}
              </Layout.Item>
            </Layout>

            {isCreatingNewConversationModeEnabled ? (
              <NewConversationCard
                onCancel={() => setIsCreatingNewConversationModeEnabled(false)}
                onCreate={handleCreateConversation}
                titleOptions={titleOptions}
                isCreatingNewConversationRequestPending={isCreatingNewConversationRequestPending}
              />
            ) : (
              <Conversation
                activeConversation={activeConversation}
                isUpdatingTitle={isUpdatingTitle}
                updateConversationTitle={updateConversationTitle}
                updateConversationStatus={updateConversationStatus}
                isUpdatingStatus={isUpdatingStatus}
                groupedMessages={groupedMessages}
                messageInputRef={messageInputRef}
                handleAddMessage={handleAddMessage}
                titleOptions={titleOptions}
              />
            )}
          </Layout.Item>
        </Layout>
      </PageWrap>
    </>
  );
};
